import { GenericAbortSignal } from 'axios';

import {
  AdminRunHealth,
  AdminRunLog,
  AdminRunValue,
  AdminWorkflowRun,
  AdminWorkflowRunDetail,
} from 'models/AdminWorkflowRun';
import { Paginated } from 'models/Paginated';
import { BaseIndexQueryParams } from 'services/api/types';
import http from 'services/http';

export type AdminWorkflowRunsIndexParams = BaseIndexQueryParams & {
  clientId?: string;
  definitionApiName?: string;
  status?: string;
  outcome?: string;
  // Neither is a column. Whether a run is stuck, and why, is computed per run by the API, so both
  // are applied before the query builder rather than by it.
  stuck?: boolean;
  stuckReason?: string;
};

export const adminWorkflowRunsQueryKeys = {
  base: ['adminWorkflowRuns'],
  index: (params: Record<string, any> = {}) => ['adminWorkflowRuns', 'index', params],
  show: (id: string) => ['adminWorkflowRuns', 'show', id],
  values: (id: string) => ['adminWorkflowRuns', 'values', id],
  logs: (id: string) => ['adminWorkflowRuns', 'logs', id],
  health: () => ['adminWorkflowRuns', 'health'],
};

export async function indexAdminWorkflowRuns(params: AdminWorkflowRunsIndexParams, signal?: GenericAbortSignal) {
  return http
    .get<Paginated<AdminWorkflowRun>>('/admin/workflow_runs', { params, signal })
    .then((response) => response.data);
}

export async function showAdminWorkflowRun(id: string, signal?: GenericAbortSignal) {
  return http.get<AdminWorkflowRunDetail>(`/admin/workflow_runs/${id}`, { signal }).then((response) => response.data);
}

// Values and logs are their own requests because both are unbounded in a way the run header is
// not, so the page paints before either lands.
export async function indexAdminWorkflowRunValues(id: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: AdminRunValue[] }>(`/admin/workflow_runs/${id}/values`, { signal })
    .then((response) => response.data.data);
}

export async function indexAdminWorkflowRunLogs(id: string, signal?: GenericAbortSignal) {
  return http
    .get<{ data: AdminRunLog[] }>(`/admin/workflow_runs/${id}/logs`, { signal })
    .then((response) => response.data.data);
}

export async function showAdminWorkflowRunHealth(signal?: GenericAbortSignal) {
  return http.get<AdminRunHealth>('/admin/workflow_runs/health', { signal }).then((response) => response.data);
}
